import { createFileRoute } from '@tanstack/react-router';
import { useOrganization } from '@/hooks/useOrganization';
import { useProjects } from '@/hooks/useProjects';
import { useOrganizationSubscriptionStatus } from '@/hooks/useOrganizationSubscription';
import { useAuditLogs } from '@/hooks/useAuditLogs';
import { useOrganizationMembers } from '@/hooks/useOrganizationMembers';
import { useOrganizationLimits } from '@/hooks/useOrganizationLimits';
import { UpgradeBanner } from '@/components/plans/UpgradeBanner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { ApiProject, ProjectsResponse } from '@envase/api/client';
import { loadAuthGuard } from '@/lib/auth-helpers';

export const Route = createFileRoute('/_authenticated/org/$orgId/')({
  loader: loadAuthGuard<{ orgId: string }>(async ({ params, context, ensureQueryData, sessionReady }) => {
    const { orgId } = params;
    const { trpc } = context;

    if (sessionReady) {
      await ensureQueryData(
        trpc.organizations.getAll.queryOptions(undefined),
      );
    }

    return { orgId };
  }),
  component: OrganizationOverview,
});

function OrganizationOverview() {
  const { orgId } = Route.useParams();
  const { organization } = useOrganization(orgId);
  const { data, isLoading: projectsLoading } = useProjects();
  const { data: subscriptionData } = useOrganizationSubscriptionStatus(orgId);
  // Get organization limits (API requests count)
  const { data: limitsData } = useOrganizationLimits(organization?.id);
  const apiRequestsCount = limitsData?.requests?.current || 0;

  // Get recent activity (audit logs)
  const { data: auditData } = useAuditLogs(organization?.slug || '', { 
    dateRange: '7d', // Last 7 days
  });
  const recentActivity = auditData?.logs?.slice(0, 5) || [];

  // Get members count
  const { data: membersData } = useOrganizationMembers(organization?.slug || '');
  const membersCount = membersData?.members?.length || 0;

  const allProjects: ApiProject[] = (data as ProjectsResponse | undefined)?.projects || [];
  
  // Filter projects by organization
  const orgProjects = allProjects.filter((project: ApiProject) => project.organizationId === organization?.id);

  return (
    <div className="space-y-8">
      {/* Upgrade Banner for basic plans */}
      {subscriptionData?.shouldShowPrompts && (
        <UpgradeBanner organizationId={orgId} currentPlan="free" />
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Members</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{membersCount}</div>
            <p className="text-xs text-muted-foreground">Active organization members</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">API Requests</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{apiRequestsCount.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">This month</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Projects</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{orgProjects.length}</div>
            <p className="text-xs text-muted-foreground">Active projects</p>
          </CardContent>
        </Card>
      </div>

      {/* Panel de tarjetas de proyectos */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Proyectos en esta organización</h2>
        {projectsLoading ? (
          <div className="py-8 text-center text-gray-400">Cargando proyectos...</div>
        ) : orgProjects.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {orgProjects.map((project: ApiProject) => (
              <a
                key={project.id}
                href={`/org/${orgId}/projects/${project.id}`}
                className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition flex flex-col"
              >
                <h3 className="text-lg font-semibold mb-2 truncate">{project.name}</h3>
                <p className="text-gray-600 text-sm line-clamp-2">{project.description || 'Sin descripción'}</p>
                <div className="mt-4 flex items-center justify-between text-sm">
                  <span className="text-gray-400">/{project.slug}</span>
                  <span className="text-purple-600 font-medium">Ver →</span>
                </div>
              </a>
            ))}
          </div>
        ) : (
          <div className="py-8 text-center text-gray-400">No hay proyectos en esta organización</div>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
          <CardDescription>Latest events in this organization</CardDescription>
        </CardHeader>
        <CardContent>
          {recentActivity.length > 0 ? (
            <div className="space-y-4">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-medium">{activity.action}</p>
                    <p className="text-xs text-muted-foreground">
                      {activity.resource} • {new Date(activity.timestamp).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{activity.user.name}</span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      activity.severity === 'critical' ? 'bg-red-100 text-red-800' :
                      activity.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                      activity.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {activity.severity}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-600 text-sm">No recent activity</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

